import {
  Alert, Button, Card, DatePicker, Descriptions, InputNumber, Modal, Space, Table, Typography,
} from 'antd';
import rentalApi from 'api/rentalApi';
import ToastHelper from 'general/helpers/ToastHelper';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { formatDate, formatDateTime, formatMoney, getRentalErrorMessage } from '../../rentalConstants';

const { Text, Title } = Typography;
const { RangePicker } = DatePicker;

function RentalReconciliationScreen() {
  const [rentalPartnerId, setRentalPartnerId] = useState(null);
  const [range, setRange] = useState([dayjs().startOf('month'), dayjs()]);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [approveLoading, setApproveLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [history, setHistory] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const response = await rentalApi.getReconciliations({ rentalPartnerId: rentalPartnerId || undefined });
      setHistory(response?.reconciliations || []);
      setHistoryTotal(response?.total || 0);
    } catch (error) {
      setHistory([]);
      setHistoryTotal(0);
      ToastHelper.showError(getRentalErrorMessage(error));
    } finally {
      setHistoryLoading(false);
    }
  }, [rentalPartnerId]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const loadPreview = async () => {
    if (!rentalPartnerId || !range?.[0] || !range?.[1]) {
      ToastHelper.showError('Nhập mã đối tác và khoảng thời gian trước');
      return;
    }
    setPreviewLoading(true);
    try {
      const response = await rentalApi.previewReconciliation({
        rentalPartnerId,
        periodStart: range[0].format('YYYY-MM-DD'),
        periodEnd: range[1].format('YYYY-MM-DD'),
      });
      setPreview(response?.preview || null);
    } catch (error) {
      setPreview(null);
      ToastHelper.showError(getRentalErrorMessage(error));
    } finally {
      setPreviewLoading(false);
    }
  };

  const submitApprove = async () => {
    setApproveLoading(true);
    try {
      await rentalApi.createAndApproveReconciliation({
        rentalPartnerId,
        periodStart: range[0].format('YYYY-MM-DD'),
        periodEnd: range[1].format('YYYY-MM-DD'),
      });
      ToastHelper.showSuccess('Đã duyệt đối soát và cộng ví đối tác');
      setConfirmOpen(false);
      setPreview(null);
      loadHistory();
    } catch (error) {
      ToastHelper.showError(getRentalErrorMessage(error));
    } finally {
      setApproveLoading(false);
    }
  };

  const historyColumns = useMemo(() => [
    { title: 'Mã kỳ', dataIndex: 'reconciliationCode', width: 140, render: (value) => <Text strong>{value}</Text> },
    { title: 'Đối tác', key: 'partner', render: (_, row) => row.partner?.contactName || `#${row.rentalPartnerId}` },
    { title: 'Kỳ', key: 'period', render: (_, row) => `${formatDate(row.periodStart)} → ${formatDate(row.periodEnd)}` },
    { title: 'Số đơn', dataIndex: 'orderCount', width: 90 },
    { title: 'Tổng doanh thu', dataIndex: 'totalGrossFare', width: 140, render: formatMoney },
    { title: 'Chia đối tác', dataIndex: 'totalPartnerShare', width: 140, render: formatMoney },
    { title: 'Trạng thái', dataIndex: 'status', width: 120 },
    { title: 'Duyệt lúc', dataIndex: 'approvedAt', width: 170, render: formatDateTime },
  ], []);

  return (
    <div className="container-fluid px-4 my-4">
      <Card className="mb-4">
        <Title level={4}>Đối soát doanh thu cho thuê xe</Title>
        <Space wrap className="mb-3">
          <InputNumber
            placeholder="Mã đối tác (rentalPartnerId)"
            value={rentalPartnerId}
            onChange={setRentalPartnerId}
            min={1}
            style={{ width: 220 }}
          />
          <RangePicker value={range} onChange={setRange} />
          <Button type="primary" onClick={loadPreview} loading={previewLoading}>Xem trước</Button>
        </Space>

        {preview && (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="Số đơn">{preview.orderCount}</Descriptions.Item>
              <Descriptions.Item label="Tổng doanh thu">{formatMoney(preview.totalGrossFare)}</Descriptions.Item>
              <Descriptions.Item label="Chia đối tác (20%)">{formatMoney(preview.totalPartnerShare)}</Descriptions.Item>
              <Descriptions.Item label="Kỳ">{preview.periodStart} → {preview.periodEnd}</Descriptions.Item>
            </Descriptions>
            {preview.orderCount === 0 ? (
              <Alert type="warning" showIcon message="Không có đơn nào cần đối soát trong kỳ này" />
            ) : (
              <Button type="primary" onClick={() => setConfirmOpen(true)}>
                Duyệt kỳ này & cộng ví đối tác
              </Button>
            )}
          </Space>
        )}
      </Card>

      <Card>
        <Title level={5}>Lịch sử đối soát</Title>
        <Table
          rowKey="rentalReconciliationId"
          loading={historyLoading}
          dataSource={history}
          columns={historyColumns}
          scroll={{ x: 1000 }}
          locale={{ emptyText: 'Không có dữ liệu' }}
          pagination={{ total: historyTotal, pageSize: 20 }}
        />
      </Card>

      <Modal
        title="Xác nhận duyệt đối soát"
        open={confirmOpen}
        okText="Duyệt & cộng ví"
        cancelText="Huỷ"
        confirmLoading={approveLoading}
        onCancel={() => setConfirmOpen(false)}
        onOk={submitApprove}
      >
        <Text>
          Sẽ cộng <Text strong>{formatMoney(preview?.totalPartnerShare)}</Text> vào ví đối tác #{rentalPartnerId}
          {' '}cho {preview?.orderCount} đơn trong kỳ {preview?.periodStart} → {preview?.periodEnd}. Hành động này
          không thể hoàn tác.
        </Text>
      </Modal>
    </div>
  );
}

export default RentalReconciliationScreen;
